/**
 * Client-side table export: CSV download and a printable report.
 * Both take already-formatted cells, so callers keep their own labels and number formats.
 */

export type ExportCell = string | number | null | undefined

const cellText = (v: ExportCell) => (v == null || v === "" ? "-" : String(v))

/** UTF-8 CSV with a BOM so Excel opens Cyrillic correctly. */
export function downloadCsv(fileName: string, header: string[], rows: ExportCell[][]) {
  const line = (cells: ExportCell[]) => cells.map((c) => `"${cellText(c).replace(/"/g, '""')}"`).join(",")
  const csv = "﻿" + [line(header), ...rows.map(line)].join("\n")
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }))
  const a = document.createElement("a")
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

const escapeHtml = (v: ExportCell) => cellText(v).replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!)

type PrintReport = {
  title: string
  subtitle?: string
  header: string[]
  rows: ExportCell[][]
  /** Column indexes printed right-aligned (amounts, days). */
  numeric?: number[]
  /** Optional last row, e.g. «Нийт · 7 нэхэмжлэх ... 6,197,060.00». */
  footer?: ExportCell[]
}

/**
 * Prints a plain report through a hidden iframe. Intentional exception to the Mantine-only
 * rule: the iframe is a print document outside the app theme, so it is built as HTML with
 * its own minimal stylesheet; font and colours are copied from the app's theme variables.
 */
export function printReport({ title, subtitle, header, rows, numeric = [], footer }: PrintReport) {
  // The iframe has no Mantine stylesheet, so resolve the theme variables here and inline their values.
  const root = getComputedStyle(document.documentElement)
  const token = (name: string, fallback: string) => root.getPropertyValue(name).trim() || fallback
  const font = token("--mantine-font-family", "sans-serif")
  const ink = token("--mantine-color-gray-9", "CanvasText")
  const sub = token("--mantine-color-gray-7", "GrayText")
  const line = token("--mantine-color-gray-2", "GrayText")
  const strong = token("--mantine-color-gray-5", "GrayText")
  const head = token("--mantine-color-gray-1", "Canvas")
  const align = (i: number) => (numeric.includes(i) ? ' class="num"' : "")
  const tr = (cells: ExportCell[], tag: "td" | "th") => `<tr>${cells.map((c, i) => `<${tag}${align(i)}>${escapeHtml(c)}</${tag}>`).join("")}</tr>`

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
    @page { size: A4 landscape; margin: 12mm; }
    body { font-family: ${font}; color: ${ink}; font-size: 11px; margin: 0; }
    h1 { font-size: 18px; margin: 0 0 4px; }
    p { margin: 0 0 12px; color: ${sub}; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 6px 8px; border-bottom: 1px solid ${line}; text-align: left; white-space: nowrap; }
    th { background: ${head}; font-weight: 600; }
    tfoot td { font-weight: 700; border-top: 1px solid ${strong}; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
  </style></head><body>
    <h1>${escapeHtml(title)}</h1>${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ""}
    <table><thead>${tr(header, "th")}</thead><tbody>${rows.map((r) => tr(r, "td")).join("")}</tbody>
    ${footer ? `<tfoot>${tr(footer, "td")}</tfoot>` : ""}</table>
  </body></html>`

  const frame = document.createElement("iframe")
  frame.setAttribute("aria-hidden", "true")
  frame.style.position = "fixed"
  frame.style.width = "0"
  frame.style.height = "0"
  frame.style.border = "0"
  document.body.appendChild(frame)
  const doc = frame.contentDocument!
  doc.open()
  doc.write(html)
  doc.close()
  const win = frame.contentWindow!
  // Firefox returns from print() before the dialog closes, so wait for afterprint.
  win.addEventListener("afterprint", () => frame.remove(), { once: true })
  win.focus()
  win.print()
}
